/**
 * Asset CSV / JSON import + export helpers.
 *
 * CSV format mirrors legacy `Step2AssetIdentification.exportAssets`:
 * header row of column labels, comma-separated value rows, arrays
 * joined with `; ` inside quoted cells.
 *
 * JSON import accepts either a top-level array of asset records or
 * `{ assets: [...] }`. Caller passes the parsed records into the
 * `crud.add` loop.
 */
import { ASSET_COLUMNS_READ, type AssetRecord } from './assetFieldSchema';

const EXPORT_COLUMNS = ASSET_COLUMNS_READ;

const escapeCsv = (raw: unknown): string => {
  if (raw == null) return '';
  if (Array.isArray(raw)) return `"${raw.join('; ').replace(/"/g, '""')}"`;
  const s = String(raw);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/** Trigger a CSV file download with `assets_YYYY-MM-DD.csv` filename. */
export function exportAssetsToCsv(assets: AssetRecord[]): void {
  const header = EXPORT_COLUMNS.map((c) => c.label).join(',');
  const rows = assets.map((a) =>
    EXPORT_COLUMNS.map((c) => escapeCsv((a as Record<string, unknown>)[c.key])).join(','),
  );
  const csv = [header, ...rows].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `assets_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
}

/** Minimal CSV parser — handles quoted fields with embedded commas + newlines.
 *  Splits the file into rows + cells, returns the cell matrix. */
function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      // End of row — flush, eat the optional \n of a \r\n pair
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.length > 0));
}

/** Build the CSV header → column key mapping. Falls back to the
 *  literal header string when it doesn't match a known label. */
function headerToKey(header: string): string {
  const col = EXPORT_COLUMNS.find((c) => c.label.toLowerCase() === header.trim().toLowerCase());
  return col?.key ?? header.trim().toLowerCase().replace(/\s+/g, '_');
}

const LIST_KEYS = new Set(['security_properties']);

/** Parse a CSV file into asset records. Splits list cells back on
 *  `;` separators for `security_properties`. */
export function parseAssetCsv(text: string): AssetRecord[] {
  const rows = parseCsvText(text);
  if (rows.length === 0) return [];
  const headers = rows[0].map(headerToKey);
  return rows.slice(1).map((row) => {
    const out: Record<string, unknown> = {};
    headers.forEach((key, i) => {
      const raw = row[i] ?? '';
      out[key] = LIST_KEYS.has(key)
        ? raw
            .split(/[;\n]/)
            .map((s) => s.trim())
            .filter(Boolean)
        : raw;
    });
    return out as AssetRecord;
  });
}

/** Parse a JSON file into asset records. Accepts `[…]` or `{ assets: [...] }`. */
export function parseAssetJson(text: string): AssetRecord[] {
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return parsed as AssetRecord[];
  if (parsed && Array.isArray(parsed.assets)) return parsed.assets as AssetRecord[];
  throw new Error('JSON must be an array of assets or `{ assets: [...] }`');
}

/** Dispatcher: pick CSV vs JSON parser by file extension. */
export async function parseAssetFile(file: File): Promise<AssetRecord[]> {
  const text = await file.text();
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'json') return parseAssetJson(text);
  if (ext === 'csv') return parseAssetCsv(text);
  // Fallback: try JSON first, then CSV
  try {
    return parseAssetJson(text);
  } catch {
    return parseAssetCsv(text);
  }
}
